/**
 * ParsleyManager
 *
 * Wraps Parsley form validation setup:
 *  - lazily loads the correct locale file via Vite's import.meta.glob
 *  - provides a small init() convenience for wiring up a form
 *
 * Note: exposing Parsley on window is handled in the entry file
 * (alongside jQuery/loadjs/etc.), not inside this class, for consistency.
 *
 * Locale files are expected at: src/js/frontend/locales/parsley/{lang}.js
 * (copy them from node_modules/parsleyjs/dist/i18n/ into that folder,
 * only for the languages you actually support)
 */
export default class ParsleyManager {
  /**
   * @param {typeof import('parsleyjs')} Parsley - the Parsley constructor/namespace
   */
  constructor(Parsley) {
    this.Parsley = Parsley;
    this.currentLocale = null;

    // Glob is resolved at build time by Vite; keys are relative paths,
    // values are lazy import functions.
    this.localeImporters = import.meta.glob('../locales/parsley/*.js');
  }

  /**
   * Loads and applies a Parsley locale, falling back silently if the
   * locale file doesn't exist (e.g. unsupported/unexpected language code).
   *
   * @param {string} lang - e.g. "en", "it", "fr"
   * @returns {Promise<boolean>} true if the locale was found and applied
   */
  async loadLocale(lang) {
    if (!lang || lang === this.currentLocale) {
      return this.currentLocale === lang;
    }

    const key = `../locales/parsley/${lang}.js`;
    const importer = this.localeImporters[key];

    if (!importer) {
      console.warn(`[ParsleyManager] No Parsley locale file found for "${lang}"`);
      return false;
    }

    await importer();
    this.Parsley.setLocale(lang);
    this.currentLocale = lang;

    return true;
  }

  /**
   * Convenience helper: loads a locale then binds Parsley validation
   * to a given form element/selector.
   *
   * @param {string|Element} form - selector string or DOM element
   * @param {string} lang
   * @param {object} [options] - Parsley options object
   */
  async init(form, lang, options = {}) {
    await this.loadLocale(lang);

    const formEl = typeof form === 'string'
      ? document.querySelector(form)
      : form;

    if (!formEl) {
      console.warn('[ParsleyManager] Form element not found:', form);
      return null;
    }

    return new this.Parsley.Factory(formEl, options);
  }
}